import { GridColumns } from '@mui/x-data-grid';
import { createRoleTypeFilterForPortalType } from '../../../hooks';
import {
    createAutocompleteField,
    createTextField,
    createUseSearchService,
    ISearchConfiguration,
    ISearchRequest,
    ISearchResponse,
    ISearchService,
} from '../../../reusableFeatures';
import { CompanyService } from '../../../services';
import { PortalType } from '../../../types';
import { conditionalOptions } from '../../../utils';
import { createRoleTypeFilter } from './RoleType.filter';
import { RoleTypeService } from './RoleTypeService';
import { RoleTypeSearchParams } from './types';

export class RoleTypeSearchService implements ISearchService<RoleTypeSearchParams> {
    private companyService = new CompanyService();

    private roleTypeService = new RoleTypeService();

    constructor(private companyId: number | null, private portalType: PortalType) {}

    async fetchSearchConfiguration(): Promise<ISearchConfiguration<RoleTypeSearchParams>> {
        const buyerCompanyOptions =
            this.companyId === null ? await this.companyService.getAllBuyerCompanyOptions() : [];

        const gridColumns: GridColumns = [
            ...conditionalOptions(this.companyId === null, [
                {
                    field: 'companyId',
                    headerName: 'Buyer Name',
                    width: 200,
                    renderCell: (params: any) => {
                        const buyerId = params.value as number | null;
                        return buyerCompanyOptions.find((x) => x.value === buyerId)?.label;
                    },
                    sortable: true,
                },
            ]),
            {
                field: 'name',
                headerName: 'Name',
                width: 150,
                sortable: true,
            },
            {
                field: 'description',
                headerName: 'Description',
                flex: 1,
                sortable: true,
            },
        ];

        const searchFormFieldColumns = [
            [
                ...conditionalOptions(this.companyId === null, [
                    createAutocompleteField({
                        id: 'companyId',
                        label: 'Buyer Name',
                        defaultValue: null,
                        dataBindingKey: 'companyId',
                        options: buyerCompanyOptions,
                    }),
                ]),
                createTextField({
                    id: 'name',
                    dataBindingKey: 'name',
                    defaultValue: '',
                    label: 'Name',
                }),
            ],
        ];

        return {
            gridColumns,
            enabledExportingMethods: [],
            searchFormFieldColumns,
            pageSizeOptions: [10, 25, 50],
            defaultPageSize: 10,
            searchWithNoCriteria: true,
        };
    }

    private getFilterStringPrefix(): string {
        switch (this.portalType) {
            case PortalType.TransceptaManager:
            case PortalType.SupplierPortal:
                return createRoleTypeFilterForPortalType({ companyId: this.companyId!, portalType: this.portalType });
            case PortalType.CSP:
                return '';
            default:
                return '';
        }
    }

    async fetchResults(
        _searchConfiguration: ISearchConfiguration<RoleTypeSearchParams>,
        searchRequest: ISearchRequest<RoleTypeSearchParams>
    ): Promise<ISearchResponse> {
        const searchQueryState = searchRequest.searchQuery.state;

        const filterStringPrefix = this.getFilterStringPrefix();

        const roleTypeFilter = createRoleTypeFilter({ ...searchQueryState });

        const filterString = [filterStringPrefix, roleTypeFilter?.length ? `(${roleTypeFilter})` : undefined]
            .filter(Boolean)
            .join(' and ');

        const $filter = filterString || undefined;

        const response = await this.roleTypeService.getRoleTypeGridItems(
            searchRequest.pageSize,
            searchRequest.pageNumber,
            searchRequest.sort,
            $filter
        );

        return {
            pageResults: response.items,
            totalResultCount: response.count,
        };
    }

    exportData(): Promise<void> {
        throw new Error('Exporting not implemented for role type search');
    }

    get key(): string {
        return 'RoleTypeSearch';
    }
}

export const useRoleTypeSearchService = createUseSearchService(RoleTypeSearchService);
